import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { getCategoriasMaisAcessadas } from '../../../../api/reportApi';
import TableContainer from '../../../../components/TableContainerReactTable';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';

interface CategoryAccessCardProps {
  campaignId: string;
}

export interface CategoryAccessCardRef {
  refresh: () => void;
}

const CategoryAccessCard = forwardRef<CategoryAccessCardRef, CategoryAccessCardProps>(
  ({ campaignId }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    numeral.locale('pt-br');
    const numeralInteger = '0,0';

    const fetchData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const categoryData = await getCategoriasMaisAcessadas(parseInt(campaignId));
        setData(categoryData);
      } catch (err) {
        console.error('Error fetching category data:', err);
        setError('Erro ao carregar dados de categorias');
      } finally {
        setLoading(false);
      }
    }, [campaignId]);

    useEffect(() => {
      if (campaignId) {
        fetchData();
      }
    }, [campaignId, fetchData]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    if (loading) {
      return <SkeletonCard rows={2} columns={3} />;
    }

    if (error) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <div className="flex items-center">
              <div className="bg-success bg-opacity-10 rounded-full p-2 mr-3">
                <i className="ri-shield-check-line text-success text-2xl"></i>
              </div>
              <div>
                <h4 className={`${styles.cardTitle} mb-0`}>Brand Safety</h4>
                <small className="text-muted">Categorias IAB por Machine Learning</small>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center text-destructive">
              <i className="ri-error-warning-line mr-2"></i>
              {error}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <div className="flex items-center">
              <div className="bg-success bg-opacity-10 rounded-full p-2 mr-3">
                <i className="ri-shield-check-line text-success text-2xl"></i>
              </div>
              <div>
                <h4 className={`${styles.cardTitle} mb-0`}>Brand Safety</h4>
                <small className="text-muted">Categorias IAB por Machine Learning</small>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted">
              <i className="ri-information-line mr-2"></i>
              Nenhuma categoria IAB identificada
            </div>
          </CardContent>
        </Card>
      );
    }

    // Column definitions for the table
    const columns = [
      {
        header: 'Categoria IAB',
        accessorKey: 'pageCategory',
        cell: (row: any) => {
          const category = row.getValue() === 'null' ? 'Desconhecido' : row.getValue();
          const isHighValue = ['News', 'Tecnologia', 'Negócios', 'Finance'].some(high => 
            category.toLowerCase().includes(high.toLowerCase())
          );
          const isSensitive = ['Adult', 'Violência', 'Pornografia', 'Gambling', 'Alcohol'].some(sensitive => 
            category.toLowerCase().includes(sensitive.toLowerCase())
          );
          
          let icon = 'ri-folder-line';
          let color = 'text-muted';
          
          if (isHighValue) {
            icon = 'ri-star-line';
            color = 'text-success';
          } else if (isSensitive) {
            icon = 'ri-alert-line';
            color = 'text-warning';
          }
          
          return (
            <div className="flex items-center">
              <i className={`${icon} ${color} mr-2`}></i>
              <span className="font-medium">{category}</span>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Impressões',
        accessorKey: 'impression',
        cell: (row: any) => {
          const value = row.getValue() || 0;
          const total = totalImpressions;
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';

          return (
            <div className="flex items-center justify-between">
              <span className="font-semibold">{numeral(value).format(numeralInteger)}</span>
              <Badge variant="secondary" className="bg-primary bg-opacity-10 text-primary px-2 py-1">
                {percentage}%
              </Badge>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Visíveis',
        accessorKey: 'viewable',
        cell: (row: any) => {
          const viewable = row.getValue() || 0;
          return (<span className="font-semibold">{numeral(viewable).format(numeralInteger)}</span>);
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Cliques',
        accessorKey: 'click',
        cell: (row: any) => {
          const click = row.getValue() || 0;
          const impression = row.row.original.impression || 0;
          const ctr = impression > 0 ? ((click / impression) * 100).toFixed(2) : '0.00';
          return (
            <div className="flex items-center justify-between">
              <span className="font-semibold">{numeral(click).format(numeralInteger)}</span>
              <Badge
                variant="secondary"
                className="bg-info bg-opacity-10 text-info px-2 py-1 ml-2"
              >
                {ctr}%
              </Badge>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
    ];

    // Calculate metrics
    const totalImpressions = data.reduce((sum, item) => sum + (item.impression || 0), 0);
    const topCategory = data.reduce((max, item) => 
      (item.click || 0) > (max.click || 0) ? item : max, 
      data[0] || null
    );
    
    // Count sensitive categories
    const sensitiveCategories = data.filter(item => {
      const category = item.pageCategory?.toLowerCase() || '';
      return ['adult', 'violence', 'gambling', 'alcohol', 'drugs', 'weapons', 'hate', 'terrorism'].some(sensitive => 
        category.includes(sensitive)
      );
    }).length;

    return (
      <Card className={styles.reportCard}>
        <CardHeader className={styles.cardHeader}>
          <div className="flex items-center">
            <div className="bg-success bg-opacity-10 rounded-full p-2 mr-3">
              <i className="ri-shield-check-line text-success text-2xl"></i>
            </div>
            <div>
              <h4 className={`${styles.cardTitle} mb-0`}>Brand Safety</h4>
              <small className="text-muted">Categorias IAB por Machine Learning</small>
            </div>
            <div className="ml-auto">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <i
                      className="ri-question-line text-muted text-lg cursor-help"
                    ></i>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Classificação automática das páginas em categorias IAB usando Machine Learning para garantir brand safety.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="text-center p-3 bg-muted rounded-lg border">
              <div className="bg-primary bg-opacity-10 rounded-full p-2 inline-flex mb-2">
                <i className="ri-folder-line text-primary"></i>
              </div>
              <h6 className="mb-1 font-semibold">{data.length}</h6>
              <small className="text-muted">Categorias Identificadas</small>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg border">
              <div className={`${sensitiveCategories > 0 ? 'bg-warning' : 'bg-success'} bg-opacity-10 rounded-full p-2 inline-flex mb-2`}>
                <i className={`ri-shield-line ${sensitiveCategories > 0 ? 'text-warning' : 'text-success'}`}></i>
              </div>
              <h6 className={`mb-1 font-semibold ${sensitiveCategories > 0 ? 'text-warning' : 'text-success'}`}>
                {sensitiveCategories}
              </h6>
              <small className="text-muted">Categorias Sensíveis</small>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg border">
              <div className="bg-info bg-opacity-10 rounded-full p-2 inline-flex mb-2">
                <i className="ri-trophy-line text-info"></i>
              </div>
              <h6 className="mb-1 font-semibold truncate" title={topCategory?.pageCategory || 'N/A'}>
                {topCategory.pageCategory === 'null' ? 'Desconhecido' : topCategory.pageCategory}
              </h6>
              <small className="text-muted">Categoria Principal</small>
            </div>
          </div>

          {/* Table */}
          <TableContainer
            divClass="table-responsive"
            columns={columns}
            data={data}
            isGlobalFilter={false}
            customPageSize={10}
            initialSorting={[{ id: 'impression', desc: true }]}
          />
        </CardContent>
      </Card>
    );
  }
);

CategoryAccessCard.displayName = 'CategoryAccessCard';

export default CategoryAccessCard;
